import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { OnboardingMachineContext } from 'components/onboarding/onboarding.machine';
import { FeatureHighlightsView } from 'components/onboarding/views/feature-highlights-view.component';
import { AccountSetupView } from 'components/onboarding/views/account-setup-view.component';
import { TermsAndConditionsView } from 'components/onboarding/views/terms-and-conditions-view.component';
import { AllSetView } from 'components/onboarding/views/all-set-view.component';
import { PlanSwitcherStepView } from 'components/onboarding/views/plan-switcher-step-view.component';
import { ReturningPlanSwitcherView } from 'components/onboarding/views/returning-plan-switcher-view.component';
import { PlanSwitcherErrorView } from 'components/onboarding/views/plan-switcher-error-view.component';
import { IneligibleMemberView } from 'components/onboarding/views/ineligible-member-view.component';
import { BasePage } from 'components/base-page/base-page.component';

// Exported for testing: the gate's state-to-render mapping is the no-flash
// contract, and driving it directly with a machine in a known state is far
// cheaper than stepping the whole onboarding flow through its actors.
export const OnboardingGate = ({ children }: { children: React.ReactNode }) => {
  const snapshot = OnboardingMachineContext.useSelector((s) => s);

  if (
    snapshot.matches('checkingEligibility') ||
    snapshot.matches('initializing') ||
    snapshot.matches('acceptingTerms') ||
    snapshot.matches('resolvingPlanSelection')
  ) {
    return <LoadingIndicator />;
  }

  // Checked before everything else, so this returns ahead of the
  // onboarding and bootstrap states below it.
  if (snapshot.matches('ineligible')) {
    return <IneligibleMemberView />;
  }

  // Both bootstrap failure signals render the same server-driven screen
  // (GEIS-621): `no_plans` and the failed-closed `error`. The distinction stays
  // in the machine for analytics and for the transitions, but the member sees
  // one designed screen either way.
  if (
    snapshot.matches('noCoverage') ||
    snapshot.matches('planSelectionError')
  ) {
    return <PlanSwitcherErrorView />;
  }

  if (snapshot.matches('featureHighlights')) {
    return <FeatureHighlightsView />;
  }

  if (snapshot.matches('accountSetup')) {
    return <AccountSetupView />;
  }

  if (snapshot.matches('termsAndConditions')) {
    return <TermsAndConditionsView />;
  }

  if (snapshot.matches('planSwitcher')) {
    return <PlanSwitcherStepView />;
  }

  if (snapshot.matches('returningPlanSwitcher')) {
    return <ReturningPlanSwitcherView />;
  }

  if (snapshot.matches('allSet')) {
    return <AllSetView />;
  }

  if (snapshot.matches('error')) {
    // Surface the failure to the nearest ObservabilityErrorBoundary, with
    // the underlying actor error attached as `cause` so the rejection
    // reason (typically the user-profile socket failure) is preserved in
    // Sentry instead of a generic "Onboarding error" string.
    const upstream = snapshot.context.errorCause;
    throw new Error('Onboarding error', upstream ? { cause: upstream } : {});
  }

  return <BasePage>{children}</BasePage>;
};

export const OnboardingContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <OnboardingMachineContext.Provider>
    <OnboardingGate>{children}</OnboardingGate>
  </OnboardingMachineContext.Provider>
);
